/**
 * The queue the planner will walk, on screen, through the engine's own rule
 * (`applyManualOrder` — the given lists first, the rest in the default
 * order): MANUAL = the dragged sequence, FORMULA = the ranked lists best
 * first, unranked ones after in the default (biggest-first) order.
 * For Media Devoted | Created By Ciro Autiero
 */
import { applyManualOrder, type DropGenListRankingPreview, hourRangeLabel } from '@trafficpulse/shared';

import type { DraftGroupRef } from '../../../../drop-generator.types';

import { excludeHit } from '../../rules/rule-model';

interface QueueRow {
  wiserId: number;
}

export function queueOrder<T extends QueueRow>(
  byDefault: T[],
  link: Pick<DraftGroupRef, 'orderMode' | 'manualOrderJson'>,
  preview: DropGenListRankingPreview | undefined,
): T[] {
  const ids = byDefault.map((r) => r.wiserId);
  const first =
    link.orderMode === 'MANUAL'
      ? (link.manualOrderJson ?? [])
      : link.orderMode === 'FORMULA' && preview
        ? preview.lists.filter((l) => !l.unranked).map((l) => l.wiserId)
        : [];
  if (first.length === 0) return byDefault;
  const byId = new Map(byDefault.map((r) => [r.wiserId, r]));
  return applyManualOrder(ids, first).map((id) => byId.get(id)!);
}

const fmt = (n: number) => n.toLocaleString('en-US');

/** What a queue row says about the formula — its score, or why it is unranked. */
export function formulaNote(wiserId: number, preview: DropGenListRankingPreview): string {
  const l = preview.lists.find((x) => x.wiserId === wiserId);
  const range = hourRangeLabel(preview.primaryRangeKey);
  if (!l || l.uniqueVisits === 0) return `no traffic in ${range} — unranked`;
  if (l.unranked) return `excluded — ${excludeHit(l.ruleHits)?.reason ?? `${fmt(l.uniqueVisits)} unique visits`}`;
  return `score ${l.score!.toFixed(2)} · ${fmt(l.uniqueVisits)} unique visits`;
}
